import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingDef, settingProblem, type ClientSettingsView, type SettingRowView, type TemplateSummaryView } from '@lp/contracts';
import { useState } from 'react';
import { Link } from 'react-router';
import { errorText, fieldProblems, get, post, put } from '@/lib/api';
import { useScope } from '@/lib/scope';
import { dateTime } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { SettingValueText, SourceBadge, SOURCE_LABEL } from './settings';
import { Badge, Button, Card, Dialog, ErrorBox, Field, Input, Loading, Select } from './ui';

/**
 * A client's configuration (master prompt §10): every setting with its CURRENT VALUE, its SOURCE and whether the company overrides it —
 * with the template it is on. Only the Super Admin changes a value or resets it to the inherited one.
 */
export function ClientSettings({ clientId }: { clientId: number }) {
  const t = useT();
  const qc = useQueryClient();
  const scope = useScope();
  // the Super Admin changes values and templates; a company-level user with Settings edit changes its own values; everyone else reads
  const superAdmin = scope.can('settings', 'edit') && scope.companyLevel;
  const platformAdmin = !scope.company && superAdmin;
  const [onlyOwn, setOnlyOwn] = useState(false);
  const key = ['client', clientId, 'settings'];
  const view = useQuery({ queryKey: key, queryFn: () => get<ClientSettingsView>(`${scope.base}/settings`) });
  const templates = useQuery({ queryKey: ['templates'], queryFn: () => get<TemplateSummaryView[]>('/platform/templates'), enabled: platformAdmin });
  const [pick, setPick] = useState('');
  const [editing, setEditing] = useState<SettingRowView | null>(null);
  const done = (v: ClientSettingsView) => {
    qc.setQueryData(key, v);
    void qc.invalidateQueries({ queryKey: ['templates'] });
  };
  const give = useMutation({ mutationFn: (x: { templateCode: string | null; version?: number }) => put<ClientSettingsView>(`/platform/clients/${clientId}/template`, x), onSuccess: (v) => { setPick(''); done(v); } });
  const reset = useMutation({ mutationFn: (r: SettingRowView) => post<ClientSettingsView>(`${scope.base}/settings/reset`, { key: r.key, productCode: r.productCode }), onSuccess: done });
  const tpl = view.data?.template ?? null;
  const offered = (templates.data ?? []).filter((x) => x.status === 'active' && x.publishedVersion && x.code !== tpl?.code);

  return (
    <Card title={t('Configuration')} className="mt-4">
      {view.isLoading ? (
        <Loading />
      ) : view.error || !view.data ? (
        <ErrorBox text={errorText(view.error)} />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2 text-sm" data-testid="client-template">
            <span className="text-ink-500">{t('Template')}:</span>
            {tpl ? (
              <>
                <Link to={`/templates/${tpl.code}`} className="font-medium text-ink-900 hover:underline">
                  {tpl.nameEn}
                </Link>
                <Badge>v{tpl.version}</Badge>
                {tpl.newerVersion && (
                  <Badge tone="blue">
                    {t('Newer')}: v{tpl.newerVersion}
                  </Badge>
                )}
              </>
            ) : (
              <span className="text-ink-500">{t('None')}</span>
            )}
            {platformAdmin && (
              <span className="ml-auto flex flex-wrap items-center gap-2">
                {tpl?.newerVersion && (
                  <Button variant="secondary" onClick={() => give.mutate({ templateCode: tpl.code, version: tpl.newerVersion! })} disabled={give.isPending}>
                    {t('Move to')} v{tpl.newerVersion}
                  </Button>
                )}
                {offered.length > 0 && (
                  <>
                    <Select value={pick} onChange={(e) => setPick(e.target.value)} aria-label={t('Give a template')}>
                      <option value="">{t('Give a template')}</option>
                      {offered.map((x) => (
                        <option key={x.code} value={x.code}>
                          {x.nameEn} ({x.code}) v{x.publishedVersion}
                        </option>
                      ))}
                    </Select>
                    <Button onClick={() => pick && give.mutate({ templateCode: pick })} disabled={!pick || give.isPending}>
                      {t('Give')}
                    </Button>
                  </>
                )}
                {tpl && (
                  <Button variant="ghost" onClick={() => give.mutate({ templateCode: null })} disabled={give.isPending}>
                    {t('No template')}
                  </Button>
                )}
              </span>
            )}
          </div>
          {give.error && <ErrorBox text={errorText(give.error)} />}
          <label className="mb-2 flex items-center gap-1.5 text-xs text-ink-700">
            <input type="checkbox" checked={onlyOwn} onChange={(e) => setOnlyOwn(e.target.checked)} />
            {t('Only the company’s own values (overrides)')}
          </label>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-left text-xs font-medium text-ink-500">
                  <th className="py-1.5 pr-3">{t('Setting')}</th>
                  <th className="py-1.5 pr-3">{t('Product')}</th>
                  <th className="py-1.5 pr-3 text-right">{t('Current value')}</th>
                  <th className="py-1.5 pr-3">{t('Source')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {view.data.rows.filter((r) => !onlyOwn || r.overridden).map((r) => (
                  <tr key={`${r.key}@${r.productCode ?? ''}`} className="border-b border-ink-100 last:border-0" data-testid={`setting-${r.key}${r.productCode ? `@${r.productCode}` : ''}`}>
                    <td className="py-2 pr-3">{t(settingDef(r.key)?.label ?? r.key)}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{r.productCode ?? '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">
                      <SettingValueText settingKey={r.key} value={r.value} />
                      {r.overridden && (
                        <span className="block text-[11px] text-ink-500">
                          {t('Inherited')}: <SettingValueText settingKey={r.key} value={r.inherited.value} /> ({t(SOURCE_LABEL[r.inherited.source])})
                        </span>
                      )}
                      {r.changedAt && (
                        <span className="block text-[11px] text-ink-400" data-testid="changed">
                          {r.changedBy ?? t('System')} · {dateTime(r.changedAt)}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <SourceBadge source={r.source} />
                    </td>
                    <td className="py-2 text-right">
                      {superAdmin && (
                        <span className="inline-flex gap-1">
                          {r.canOverride ? (
                            <Button variant="ghost" onClick={() => setEditing(r)}>
                              {t('Change')}
                            </Button>
                          ) : (
                            <span className="px-2 text-xs text-ink-400" title={t('The client’s package does not include this')}>
                              {t('Not in the package')}
                            </span>
                          )}
                          {r.overridden && (
                            <Button variant="ghost" onClick={() => reset.mutate(r)} disabled={reset.isPending}>
                              {t('Reset to inherited')}
                            </Button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {reset.error && <ErrorBox text={errorText(reset.error)} />}
          {editing && <OverrideDialog row={editing} onDone={(v) => { setEditing(null); done(v); }} onClose={() => setEditing(null)} />}
        </>
      )}
    </Card>
  );
}

function OverrideDialog({ row, onDone, onClose }: { row: SettingRowView; onDone: (v: ClientSettingsView) => void; onClose: () => void }) {
  const t = useT();
  const { base } = useScope();
  const def = settingDef(row.key)!;
  const [value, setValue] = useState(row.value == null ? '' : String(row.value));
  const parsed: number | string = def.kind === 'int' ? Number(value) : value;
  const problem = def.kind === 'int' && value === '' ? t('Type a value') : settingProblem(def, parsed);
  const save = useMutation({ mutationFn: () => put<ClientSettingsView>(`${base}/settings`, { key: row.key, productCode: row.productCode, value: parsed }), onSuccess: onDone });
  return (
    <Dialog
      title={`${t(def.label)}${row.productCode ? ` — ${row.productCode}` : ''}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => save.mutate()} disabled={!!problem || save.isPending}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-500">{t(def.help)}</p>
      <p className="text-xs">
        {t('Inherited')}: <SettingValueText settingKey={row.key} value={row.inherited.value} /> ({t(SOURCE_LABEL[row.inherited.source])})
      </p>
      <Field label={t('The company’s own value')} error={value !== '' ? problem : null}>
        <Input value={value} onChange={(e) => setValue(e.target.value)} type={def.kind === 'int' ? 'number' : 'text'} autoFocus />
      </Field>
      {save.error ? <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} /> : null}
    </Dialog>
  );
}
